import { useState, type FormEvent } from 'react';
import type { QaScorecard } from '@arcnet/contracts';
import {
  useDeleteQaScorecard, useQaPrompts, useQaScorecards, useSaveQaPrompt, useSaveQaScorecard,
} from '../../lib/api/hooks-qa.js';
import { useSession } from '../../lib/auth/session.js';
import { Badge, CardHead, Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * What this client counts as a good call (docs/37 §3.2): their scorecards, and their own
 * guidance for each analysis.
 *
 * A question carries its own guidance -- what counts, what does not -- because "did the agent
 * verify the caller?" means something different at a bank and at a pizza shop, and the model can
 * only be as consistent as the question it is asked.
 */

interface Draft {
  kind: 'question' | 'phrase';
  question: string; look_for: string; does_not_count: string;
  scale: 'yes_no' | 'zero_five'; weight: string; auto_fail: boolean; evidence_required: boolean;
  /** A required phrase (0122): the words, who must say them, how closely, and how soon. */
  phrase: string; match_mode: 'exact' | 'close' | 'meaning'; speaker: 'agent' | 'caller' | 'either';
  within: string;
}

const blank = (): Draft => ({
  kind: 'question', question: '', look_for: '', does_not_count: '', scale: 'yes_no', weight: '10',
  auto_fail: false, evidence_required: true, phrase: '', match_mode: 'close', speaker: 'agent', within: '',
});

const blankPhrase = (): Draft => ({ ...blank(), kind: 'phrase', auto_fail: true, weight: '20' });

const P = (question: string, phrase: string, over: Partial<Draft> = {}): Draft =>
  ({ ...blankPhrase(), question, phrase, ...over });
const Q = (question: string, look_for = '', does_not_count = '', over: Partial<Draft> = {}): Draft =>
  ({ ...blank(), question, look_for, does_not_count, ...over });

/**
 * Starting points, to edit rather than to use as they are. The wording of any disclosure is the
 * client's and their regulator's: these show the SHAPE of a card that checks one, and say so.
 */
const TEMPLATES: Record<string, { name: string; pass: number; items: Draft[] }> = {
  insurance: {
    name: 'Insurance or financial services sale', pass: 90,
    items: [
      P('Recording disclosure', 'This call is being recorded for quality and training purposes',
        { within: '60', match_mode: 'close' }),
      P('Identifies as a representative of the provider', 'I am a representative of', { match_mode: 'meaning' }),
      Q('Did the agent verify the client’s identity before discussing the policy?',
        'At least two of: ID number, date of birth, policy number, address.',
        'The client giving their name. A promise to verify later.', { auto_fail: true, weight: '20' }),
      P('Cooling-off period stated', 'you have thirty one days to cancel', { match_mode: 'meaning' }),
      P('Client clearly agrees', 'yes I agree', { speaker: 'caller', match_mode: 'meaning' }),
      Q('Did the agent avoid promising anything the policy does not cover?', '',
        'Explaining what the policy does cover.', { auto_fail: true, weight: '15' }),
    ],
  },
  sales: {
    name: 'Sales call', pass: 75,
    items: [
      Q('Did the agent introduce themselves and the business?', 'Their name and the company name, early in the call.'),
      Q('Did the agent find out what the customer needs before offering anything?', 'Open questions about the customer’s situation.'),
      Q('How well were objections handled?', 'Acknowledging the concern and answering it.', 'Talking over the customer.', { scale: 'zero_five', weight: '20' }),
      Q('Did the agent ask for the sale or a clear next step?', 'A direct ask, or an agreed follow-up with a date.'),
    ],
  },
  support: {
    name: 'Support call', pass: 80,
    items: [
      Q('Did the agent verify the caller before discussing the account?', 'Account number plus one other detail.', '', { auto_fail: true }),
      Q('Was the problem understood before a fix was offered?', 'The agent restating the problem back.'),
      Q('Was the problem resolved, or a clear next step agreed?', 'A fix confirmed working, or a reference and a time.'),
      Q('Was the caller treated courteously throughout?', '', '', { scale: 'zero_five' }),
    ],
  },
};

export function Scorecards({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const cards = useQaScorecards(tenantId);
  const del = useDeleteQaScorecard(tenantId);
  const [editing, setEditing] = useState<QaScorecard | 'new' | null>(null);

  if (editing) {
    return <CardForm tenantId={tenantId} existing={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />;
  }

  return (
    <>
      <SectionHead title="Scorecards"
        sub="Your own questions, each with what counts and what does not. A rule chooses which card its calls are marked against."
        action={can('qa:write')
          ? <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={13} /> Scorecard
            </button>
          : undefined} />
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Scorecard</th><th className="arc-td-right">Questions</th>
                <th className="arc-td-right">Pass mark</th><th className="arc-td-right">Version</th><th /></tr>
            </thead>
            {cards.data === undefined ? <TableSkeleton cols={5} /> : (
              <tbody>
                {cards.data.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{c.name}</span>
                      {!c.enabled && <> <Badge tone="muted">retired</Badge></>}
                      {c.questions.some((q) => q.auto_fail) && <span className="arc-subtle"> · has auto-fail</span>}
                    </td>
                    <td className="arc-td-right arc-num">{c.questions.length}</td>
                    <td className="arc-td-right arc-num">{c.pass_mark}</td>
                    <td className="arc-td-right arc-num arc-subtle">v{c.version}</td>
                    <td className="arc-td-right">
                      {can('qa:write') && (
                        <>
                          <RowAction kind="edit" label={`Edit ${c.name}`} onClick={() => setEditing(c)} />
                          <RowAction kind="delete" label={`Remove ${c.name}`} disabled={del.isPending}
                            onClick={() => {
                              void confirmAction({ title: `Remove ${c.name}?`, body: 'One that has marked calls is retired rather than deleted, so those marks are kept.', action: 'Remove', danger: true }).then((ok) => { if (ok) del.mutate(c.id); });
                            }} />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {cards.data && cards.data.data.length === 0 && (
          <Empty icon="check" title="No scorecards"
            hint="Without one, calls get the analyses a rule asks for and no mark." />
        )}
      </div>

    </>
  );
}

function CardForm({ tenantId, existing, onDone }: { tenantId: string; existing: QaScorecard | null; onDone: () => void }) {
  const save = useSaveQaScorecard(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [passMark, setPassMark] = useState(String(existing?.pass_mark ?? 80));
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [qs, setQs] = useState<Draft[]>(existing
    ? existing.questions.map((q) => ({
        kind: q.kind, question: q.question, look_for: q.look_for ?? '', does_not_count: q.does_not_count ?? '',
        scale: q.scale, weight: String(q.weight), auto_fail: q.auto_fail, evidence_required: q.evidence_required,
        phrase: q.phrase ?? '', match_mode: q.match_mode ?? 'close', speaker: q.speaker ?? 'agent',
        within: q.within_sec == null ? '' : String(q.within_sec),
      }))
    : [blank()]);
  const start = (key: string) => {
    const t = TEMPLATES[key];
    if (!t) return;
    setName(t.name); setPassMark(String(t.pass)); setQs(t.items.map((d) => ({ ...d })));
  };

  const set = (i: number, patch: Partial<Draft>) => setQs((p) => p.map((q, n) => (n === i ? { ...q, ...patch } : q)));
  const move = (i: number, by: number) => setQs((p) => {
    const next = [...p];
    const [q] = next.splice(i, 1);
    next.splice(Math.max(0, Math.min(next.length, i + by)), 0, q!);
    return next;
  });
  const totalWeight = qs.reduce((t, q) => t + (Number(q.weight) || 0), 0);

  return (
    <form onSubmit={(e: FormEvent) => {
      e.preventDefault();
      save.mutate({
        ...(existing ? { id: existing.id } : {}),
        name, enabled, pass_mark: Math.min(100, Math.max(0, Number(passMark) || 0)),
        questions: qs.filter((q) => q.question.trim().length >= 3).map((q) => ({
          kind: q.kind,
          ...(q.kind === 'phrase' ? {
            phrase: q.phrase.trim(), match_mode: q.match_mode, speaker: q.speaker,
            within_sec: q.within.trim() ? Math.min(3600, Math.max(5, Number(q.within) || 60)) : null,
          } : {}),
          question: q.question.trim(),
          look_for: q.look_for.trim() || null,
          does_not_count: q.does_not_count.trim() || null,
          scale: q.scale,
          weight: Math.min(100, Math.max(1, Number(q.weight) || 1)),
          auto_fail: q.auto_fail,
          evidence_required: q.evidence_required,
        })),
      }, { onSuccess: onDone });
    }}>
      <SectionHead title={existing ? `Edit ${existing.name}` : 'New scorecard'}
        sub={existing
          ? `Saving makes version ${existing.version + 1}. Calls already marked keep the version they were marked against.`
          : 'Each question is answered from the transcript, citing the lines it relied on.'} />

      {!existing && (
        <div className="arc-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span className="arc-muted" style={{ fontSize: 12.5 }}>Start from</span>
          {Object.entries(TEMPLATES).map(([k, t]) => (
            <button key={k} type="button" className="arc-btn arc-btn-sm" onClick={() => start(k)}>{t.name}</button>
          ))}
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
            Examples to edit: the wording of a disclosure is yours and your regulator’s.
          </span>
        </div>
      )}

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) 140px auto', gap: 12, alignItems: 'end' }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="sc-name">Name</label>
            <input id="sc-name" className="arc-input" value={name} maxLength={120} required
              onChange={(e) => setName(e.target.value)} placeholder="Support calls" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="sc-pass">Pass mark (of 100)</label>
            <input id="sc-pass" className="arc-input arc-num" inputMode="numeric" value={passMark}
              onChange={(e) => setPassMark(e.target.value.replace(/[^0-9]/g, ''))} />
          </div>
          <div className="arc-row" style={{ gap: 8, paddingBottom: 6 }}>
            <Switch id="sc-on" checked={enabled} onChange={setEnabled} />
            <label htmlFor="sc-on" style={{ fontSize: 12.5 }}>In use</label>
          </div>
        </div>
      </div>

      {qs.map((q, i) => (
        <div key={i} className="arc-card arc-card-body" style={{ marginBottom: 10 }}>
          <div className="arc-row" style={{ gap: 8, marginBottom: 8 }}>
            <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>
              {q.kind === 'phrase' ? 'Required phrase' : 'Question'} {i + 1}
            </span>
            <span className="arc-subtle" style={{ fontSize: 12 }}>
              {totalWeight > 0 ? `${Math.round(((Number(q.weight) || 0) / totalWeight) * 100)}% of the mark` : ''}
            </span>
            <div className="arc-spacer" />
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={i === 0}
              onClick={() => move(i, -1)} aria-label="Move up"><Icon name="chevronUp" size={13} /></button>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={i === qs.length - 1}
              onClick={() => move(i, 1)} aria-label="Move down"><Icon name="chevronDown" size={13} /></button>
            <RowAction kind="delete" label={`Remove ${q.kind === 'phrase' ? 'required phrase' : 'question'} ${i + 1}`} disabled={qs.length === 1}
              onClick={() => setQs((p) => p.filter((_, n) => n !== i))} />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor={`sq-${i}`}>{q.kind === 'phrase' ? 'What it is' : 'Question'}</label>
            <input id={`sq-${i}`} className="arc-input" value={q.question} maxLength={500}
              onChange={(e) => set(i, { question: e.target.value })}
              placeholder={q.kind === 'phrase' ? 'Recording disclosure' : 'Did the agent verify the caller’s identity?'} />
          </div>
          {q.kind === 'phrase' ? <PhraseFields i={i} q={q} set={set} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 10 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`sl-${i}`}>What counts</label>
              <textarea id={`sl-${i}`} className="arc-input" rows={2} maxLength={1000} value={q.look_for}
                onChange={(e) => set(i, { look_for: e.target.value })}
                placeholder="Two of: account number, ID number, address." />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor={`sn-${i}`}>What does not count</label>
              <textarea id={`sn-${i}`} className="arc-input" rows={2} maxLength={1000} value={q.does_not_count}
                onChange={(e) => set(i, { does_not_count: e.target.value })}
                placeholder="The caller volunteering their name. A promise to verify later." />
            </div>
          </div>
          )}
          <div className="arc-row" style={{ gap: 14, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {q.kind === 'question' && (
            <div className="arc-field" style={{ margin: 0, width: 150 }}>
              <label className="arc-label" htmlFor={`ss-${i}`}>Answered as</label>
              <select id={`ss-${i}`} className="arc-select" value={q.scale}
                onChange={(e) => set(i, { scale: e.target.value as Draft['scale'] })}>
                <option value="yes_no">Yes or no</option>
                <option value="zero_five">0 to 5</option>
              </select>
            </div>
            )}
            <div className="arc-field" style={{ margin: 0, width: 100 }}>
              <label className="arc-label" htmlFor={`sw-${i}`}>Weight</label>
              <input id={`sw-${i}`} className="arc-input arc-num" inputMode="numeric" value={q.weight}
                onChange={(e) => set(i, { weight: e.target.value.replace(/[^0-9]/g, '') })} />
            </div>
            <label className="arc-row" style={{ gap: 6, fontSize: 12.5, paddingBottom: 8 }}>
              <input type="checkbox" checked={q.auto_fail} onChange={(e) => set(i, { auto_fail: e.target.checked })} />
              Failing this fails the call
            </label>
            {q.kind === 'question' && (
            <label className="arc-row" style={{ gap: 6, fontSize: 12.5, paddingBottom: 8 }}>
              <input type="checkbox" checked={q.evidence_required}
                onChange={(e) => set(i, { evidence_required: e.target.checked })} />
              Must cite the lines
            </label>
            )}
          </div>
        </div>
      ))}

      <div className="arc-row" style={{ gap: 8, marginTop: 6 }}>
        <button type="button" className="arc-btn arc-btn-sm" disabled={qs.length >= 50}
          onClick={() => setQs((p) => [...p, blank()])}>
          <Icon name="plus" size={13} /> Question
        </button>
        <button type="button" className="arc-btn arc-btn-sm" disabled={qs.length >= 50}
          onClick={() => setQs((p) => [...p, blankPhrase()])}>
          <Icon name="plus" size={13} /> Required phrase
        </button>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

const MODES: ReadonlyArray<readonly [Draft['match_mode'], string, string]> = [
  ['exact', 'Word for word', 'Every word, in order. Checked against the transcript, not by the AI.'],
  ['close', 'Close wording', 'The words in order, forgiving a word the transcriber misheard. Checked against the transcript.'],
  ['meaning', 'Same meaning', 'The same thing in other words. Judged by the AI, citing the line.'],
];

function PhraseFields({ i, q, set }: { i: number; q: Draft; set: (i: number, patch: Partial<Draft>) => void }) {
  return (
    <>
      <div className="arc-field" style={{ margin: '10px 0 0' }}>
        <label className="arc-label" htmlFor={`sp-${i}`}>The words</label>
        <textarea id={`sp-${i}`} className="arc-input" rows={2} maxLength={1000} value={q.phrase}
          onChange={(e) => set(i, { phrase: e.target.value })}
          placeholder="This call is being recorded for quality and training purposes." />
      </div>
      <div className="arc-row" style={{ gap: 14, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="arc-field" style={{ margin: 0, width: 160 }}>
          <label className="arc-label" htmlFor={`spk-${i}`}>Said by</label>
          <select id={`spk-${i}`} className="arc-select" value={q.speaker}
            onChange={(e) => set(i, { speaker: e.target.value as Draft['speaker'] })}>
            <option value="agent">The agent</option>
            <option value="caller">The caller</option>
            <option value="either">Either</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0, width: 170 }}>
          <label className="arc-label" htmlFor={`sm-${i}`}>How closely</label>
          <select id={`sm-${i}`} className="arc-select" value={q.match_mode}
            onChange={(e) => set(i, { match_mode: e.target.value as Draft['match_mode'] })}>
            {MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0, width: 170 }}>
          <label className="arc-label" htmlFor={`swi-${i}`}>Within (seconds)</label>
          <input id={`swi-${i}`} className="arc-input arc-num" inputMode="numeric" value={q.within} placeholder="Any time"
            onChange={(e) => set(i, { within: e.target.value.replace(/[^0-9]/g, '') })} />
        </div>
      </div>
      <p className="arc-hint" style={{ marginTop: 6 }}>{MODES.find(([k]) => k === q.match_mode)?.[2]}</p>
    </>
  );
}

const KINDS = [
  { key: 'compliance', label: 'Compliance',
    hint: 'The greeting you mandate, what must be said, what must never be said.' },
  { key: 'technical', label: 'How calls are handled',
    hint: 'What counts as too long on hold, what a clean transfer looks like for you.' },
  { key: 'sentiment', label: 'Sentiment',
    hint: 'What a good or bad outcome means in your business.' },
] as const;

/**
 * The client's own instruction block per analysis. Theirs to write; the shape of the answer is
 * ours, and the prompt says so to the model, so guidance can steer what is looked for and never
 * change what comes back.
 */
export function Guidance({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const prompts = useQaPrompts(tenantId);
  const save = useSaveQaPrompt(tenantId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  if (!prompts.data) return null;

  return (
    <div className="arc-card">
      <CardHead title="Prompts" />
      <div className="arc-card-body">
        <p className="arc-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
          Your own instructions, added to each analysis of every call: house rules, phrases that
          must never be said, what a good outcome means for you. They steer what is looked for;
          they cannot change what comes back. Words that must be said go on a scorecard as a
          required phrase, where they are checked and marked.
        </p>
        {KINDS.map((k) => {
          const value = drafts[k.key] ?? prompts.data.data[k.key] ?? '';
          const changed = drafts[k.key] !== undefined && drafts[k.key] !== (prompts.data.data[k.key] ?? '');
          return (
            <div key={k.key} className="arc-field">
              <label className="arc-label" htmlFor={`qg-${k.key}`}>{k.label}</label>
              <textarea id={`qg-${k.key}`} className="arc-input" rows={3} maxLength={4000} value={value}
                disabled={!can('qa:write')} placeholder={k.hint}
                onChange={(e) => setDrafts((p) => ({ ...p, [k.key]: e.target.value }))} />
              {changed && (
                <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" style={{ marginTop: 6 }}
                  disabled={save.isPending}
                  onClick={() => save.mutate({ kind: k.key, guidance: value },
                    { onSuccess: () => setDrafts((p) => { const n = { ...p }; delete n[k.key]; return n; }) })}>
                  Save
                </button>
              )}
            </div>
          );
        })}
        {save.error && <ErrorBanner error={save.error} />}
      </div>
    </div>
  );
}
